import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { API, formatApiErrorDetail } from "../App";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export default function ContactPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    venue_name: "",
    plan_interest: params.get("plan") || "",
    message: "",
    source: "pricing",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact/submit`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      <Header />
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex h-12 w-12 rounded-full bg-primary/10 items-center justify-center mb-4">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-medium text-foreground tracking-tight mb-2">
              Get in touch
            </h1>
            <p className="text-muted-foreground">
              Tell us about your venue and the plan you're interested in. Our team gets back within one business day.
            </p>
          </div>

          {submitted ? (
            <div
              className="bg-card border border-border rounded-2xl p-8 text-center"
              data-testid="contact-success"
            >
              <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" />
              <h2 className="text-xl font-medium text-foreground mb-2">Thanks — we'll be in touch.</h2>
              <p className="text-muted-foreground mb-6">
                We received your message and will reply to{" "}
                <span className="font-medium text-foreground">{form.email}</span> shortly.
              </p>
              <Button asChild variant="outline">
                <Link to="/">Back to homepage</Link>
              </Button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-card border border-border rounded-2xl p-6 space-y-4"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-name">Your name *</Label>
                  <Input
                    id="contact-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email">Email *</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-venue">Venue name</Label>
                  <Input
                    id="contact-venue"
                    value={form.venue_name}
                    onChange={(e) => setForm({ ...form, venue_name: e.target.value })}
                    placeholder="Optional"
                    className="mt-1"
                    data-testid="contact-venue-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-plan">Plan you're interested in</Label>
                  <Input
                    id="contact-plan"
                    value={form.plan_interest}
                    onChange={(e) => setForm({ ...form, plan_interest: e.target.value })}
                    placeholder="e.g. Professional"
                    className="mt-1"
                    data-testid="contact-plan-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="contact-message">Message *</Label>
                <Textarea
                  id="contact-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  required
                  rows={5}
                  placeholder="Tell us a bit about your venue and what you're hoping to achieve…"
                  className="mt-1"
                  data-testid="contact-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full"
                data-testid="contact-submit-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Send message
              </Button>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
